import React, { useState, useRef, useEffect, useCallback } from "react"
import Select from "../Select"

const PERIOD_OPTIONS = [
  { id: "today", name: "Aujourd'hui" },
  { id: "7d", name: "7 jours" },
  { id: "30d", name: "30 jours" },
  { id: "90d", name: "90 jours" },
]

function toInputDate(date) {
  const d = new Date(date)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")

  return `${y}-${m}-${day}`
}

function formatDisplayDate(dateStr) {
  const [y, m, d] = dateStr.split("-")

  return `${d}/${m}/${y}`
}

const MAX_RANGE_DAYS = 365

function addDays(dateStr, days) {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)

  return toInputDate(d)
}

function CustomDatePicker({ isOpen, onClose, onApply, initialRange }) {
  const today = toInputDate(new Date())
  const [startDate, setStartDate] = useState(initialRange?.startDate || today)
  const [endDate, setEndDate] = useState(initialRange?.endDate || today)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (isOpen && initialRange) {
      setStartDate(initialRange.startDate || today)
      setEndDate(initialRange.endDate || today)
    }
  }, [isOpen, initialRange, today])

  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleApply = () => {
    const effectiveEnd = endDate < startDate ? startDate : endDate

    onApply({ startDate, endDate: effectiveEnd })
  }

  const maxEndDate = (() => {
    const limit = addDays(startDate, MAX_RANGE_DAYS)

    return limit < today ? limit : today
  })()

  const minStartDate = addDays(endDate, -MAX_RANGE_DAYS)

  const isSingleDay = startDate === endDate

  return (
    <div className="ai-stats-datepicker" ref={dropdownRef}>
      <div className="ai-stats-datepicker__body">
        <div className="ai-stats-datepicker__field">
          <label className="ai-stats-datepicker__label">Date de début</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            min={minStartDate}
            max={today}
            onChange={(e) => {
              const newStart = e.target.value
              setStartDate(newStart)
              if (newStart > endDate) setEndDate(newStart)
              const limit = addDays(newStart, MAX_RANGE_DAYS)
              if (endDate > limit) setEndDate(limit < today ? limit : today)
            }}
          />
        </div>
        <div className="ai-stats-datepicker__field">
          <label className="ai-stats-datepicker__label">Date de fin</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            min={startDate}
            max={maxEndDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <p className="ai-stats-datepicker__hint">
          {isSingleDay
            ? `Journée du ${formatDisplayDate(startDate)}`
            : `Du ${formatDisplayDate(startDate)} au ${formatDisplayDate(endDate)}`}
        </p>
      </div>
      <div className="ai-stats-datepicker__footer">
        <button type="button" className="btn btn-sm btn-default" onClick={onClose}>
          Annuler
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={handleApply}>
          Appliquer
        </button>
      </div>
    </div>
  )
}

export default function StatsFilters({
  totems,
  selectedTotemIds,
  onTotemChange,
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
}) {
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const containerRef = useRef(null)
  const totemOptions = totems.map((t) => ({ id: t.id, name: t.name || t.id }))
  const selectedOptions = totemOptions.filter((opt) => selectedTotemIds.includes(opt.id))

  const handlePresetClick = useCallback(
    (id) => {
      setDatePickerOpen(false)
      onPeriodChange(id)
    },
    [onPeriodChange]
  )

  const handleCustomApply = useCallback(
    (range) => {
      setDatePickerOpen(false)
      onCustomRangeChange(range)
      onPeriodChange("custom")
    },
    [onPeriodChange, onCustomRangeChange]
  )

  const customLabel =
    period === "custom" && customRange
      ? customRange.startDate === customRange.endDate
        ? formatDisplayDate(customRange.startDate)
        : `${formatDisplayDate(customRange.startDate)} – ${formatDisplayDate(customRange.endDate)}`
      : null

  return (
    <div className="ai-stats-filters">
      <div className="ai-stats-filters__header">
        <i className="fas fa-sliders-h ai-stats-filters__icon"></i>
        <span className="ai-stats-filters__title">Filtres</span>
      </div>
      <div className="ai-stats-filters__body">
        <div className="ai-stats-filters__group ai-stats-filters__group--screens">
          <label className="ai-stats-filters__label">Écrans</label>
          <Select
            onChange={(options) => onTotemChange(options.map((o) => o.id))}
            options={totemOptions}
            defaultValue={selectedOptions}
            isMulti={true}
            name="totemFilter"
          />
        </div>
        <div className="ai-stats-filters__group">
          <label className="ai-stats-filters__label">Période</label>
          <div className="d-flex align-items-start" ref={containerRef}>
            <div className="btn-group" role="group">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`btn btn-sm ${period === opt.id ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handlePresetClick(opt.id)}
                >
                  {opt.name}
                </button>
              ))}
            </div>
            <div className="position-relative ml-2">
              <button
                type="button"
                className={`btn btn-sm ${period === "custom" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setDatePickerOpen((o) => !o)}
                title="Période personnalisée"
              >
                <i className="fas fa-calendar-alt"></i>
              </button>
              <CustomDatePicker
                isOpen={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                onApply={handleCustomApply}
                initialRange={customRange}
              />
            </div>
          </div>
          {customLabel && (
            <small className="text-muted mt-1 d-block">
              <i className="fas fa-calendar-check mr-1"></i>
              {customLabel}
            </small>
          )}
        </div>
      </div>
    </div>
  )
}
